// src/private/src/pages/app/clients/components/PacingBar.jsx
import React, { memo, useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { DollarFormatter } from '@src/components';

/**
 * Linear progress bar visualization for budget pacing.
 * Shows "spent so far" vs "remaining" with a "should be here" marker.
 *
 * @param {number} budgetAmount - Total budget
 * @param {number} monthToDateSpend - Spend so far
 * @param {number} pacingPercentage - Pacing % (projected / budget * 100)
 * @param {number} daysLeft - Days remaining in the month
 * @param {number} daysTotal - Total days in the month
 */
function PacingBar({
  budgetAmount = 0,
  monthToDateSpend = 0,
  projectedSpend = 0,
  budgetRemaining = 0,
  pacingPercentage = 0,
  remainingPerDay = 0,
  daysLeft = 0,
  daysTotal = 30,
}) {
  const budget = Number(budgetAmount) || 0;
  const spent = Number(monthToDateSpend) || 0;
  const pacing = Number(pacingPercentage) || 0;

  const spentPct = useMemo(() => {
    if (budget <= 0) return 0;
    return Math.min((spent / budget) * 100, 100);
  }, [budget, spent]);

  const shouldBePct = useMemo(() => {
    if (daysTotal <= 0) return 0;
    const elapsed = daysTotal - (Number(daysLeft) || 0);
    return Math.min((elapsed / daysTotal) * 100, 100);
  }, [daysTotal, daysLeft]);

  const pacingColor = useMemo(() => {
    if (pacing < 90) return '#3b82f6'; // under — blue
    if (pacing < 110) return '#22c55e'; // on track — green
    if (pacing < 120) return '#f59e0b'; // elevated — amber
    return '#ef4444'; // over — red
  }, [pacing]);

  if (budget <= 0) return null;

  return (
    <Box sx={{ mt: 2.5, width: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
        <Typography variant="overline" color="text.secondary">
          Budget Pacing
        </Typography>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: pacingColor }}
        >
          {pacing.toFixed(1)}%
        </Typography>
      </Stack>

      {/* Progress bar */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: 20,
          borderRadius: 2,
          bgcolor: 'grey.100',
          overflow: 'visible',
        }}
      >
        {/* Spent fill */}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            height: '100%',
            width: `${spentPct}%`,
            borderRadius: 2,
            bgcolor: pacingColor,
            opacity: 0.85,
            transition: 'width 400ms ease',
          }}
        />
        {/* "Should be here" marker */}
        <Box
          sx={{
            position: 'absolute',
            left: `${shouldBePct}%`,
            top: -3,
            bottom: -3,
            width: 2,
            bgcolor: 'text.primary',
            opacity: 0.5,
            zIndex: 1,
          }}
        />
      </Box>

      {/* Labels */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mt: 1 }}
        flexWrap="wrap"
        useFlexGap
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          MTD: <Typography component="span" variant="caption" fontWeight={700}><DollarFormatter value={spent} /></Typography>
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          Budget: <Typography component="span" variant="caption" fontWeight={700}><DollarFormatter value={budget} /></Typography>
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          Remaining: <Typography component="span" variant="caption" fontWeight={700}><DollarFormatter value={budgetRemaining} /></Typography>
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          Projected: <Typography component="span" variant="caption" fontWeight={700}><DollarFormatter value={projectedSpend} /></Typography>
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          Remaining/Day: <Typography component="span" variant="caption" fontWeight={700}><DollarFormatter value={remainingPerDay} /></Typography>
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          Days Left: <Typography component="span" variant="caption" fontWeight={700}>{daysLeft}</Typography>
        </Typography>
      </Stack>
    </Box>
  );
}

export default memo(PacingBar);
